import type { FactionId, FactionSelection } from '../factions';

export interface PlayerStatsSnapshot {
  readonly hull: number;
  readonly hullMax: number;
  readonly fuel: number;
  readonly fuelMax: number;
  readonly health: number;
  readonly healthMax: number;
  readonly oxygen: number;
  readonly oxygenMax: number;
}

export type StatsListener = (snapshot: PlayerStatsSnapshot) => void;

const HULL_MAX_BY_FACTION: Record<FactionId, number> = {
  miners: 100,
  pirates: 100,
  guards: 100
};

const FUEL_MAX_BY_FACTION: Record<FactionId, number> = {
  miners: 100,
  pirates: 100,
  guards: 100
};

const ASTRONAUT_HEALTH_MAX = 100;
const ASTRONAUT_OXYGEN_MAX = 100;

export class PlayerStats {
  readonly hullMax: number;
  readonly fuelMax: number;
  readonly healthMax: number = ASTRONAUT_HEALTH_MAX;
  readonly oxygenMax: number = ASTRONAUT_OXYGEN_MAX;

  private _hull: number;
  private _fuel: number;
  private _health: number;
  private _oxygen: number;
  private readonly listeners = new Set<StatsListener>();

  constructor(selection: FactionSelection) {
    this.hullMax = HULL_MAX_BY_FACTION[selection.factionId];
    this.fuelMax = FUEL_MAX_BY_FACTION[selection.factionId];
    this._hull = this.hullMax;
    this._fuel = this.fuelMax;
    this._health = this.healthMax;
    this._oxygen = this.oxygenMax;
  }

  get hull(): number {
    return this._hull;
  }

  get fuel(): number {
    return this._fuel;
  }

  get health(): number {
    return this._health;
  }

  get hullRatio(): number {
    return this._hull / this.hullMax;
  }

  get fuelRatio(): number {
    return this._fuel / this.fuelMax;
  }

  get healthRatio(): number {
    return this._health / this.healthMax;
  }

  get oxygen(): number {
    return this._oxygen;
  }

  get oxygenRatio(): number {
    return this._oxygen / this.oxygenMax;
  }

  /** Reduce oxygen — used by the underwater system in `GameDirector`. */
  consumeOxygen(amount: number): void {
    if (amount <= 0) return;
    const previous = this._oxygen;
    this._oxygen = Math.max(0, previous - amount);
    if (this._oxygen !== previous) this.emit();
  }

  /** Refill oxygen — used when the player surfaces. */
  restoreOxygen(amount: number): void {
    if (amount <= 0) return;
    const previous = this._oxygen;
    this._oxygen = Math.min(this.oxygenMax, previous + amount);
    if (this._oxygen !== previous) this.emit();
  }

  consumeFuel(amount: number): number {
    if (amount <= 0) return 0;
    const previous = this._fuel;
    this._fuel = Math.max(0, previous - amount);
    const consumed = previous - this._fuel;
    if (consumed > 0) this.emit();
    return consumed;
  }

  addFuel(amount: number): number {
    if (amount <= 0) return 0;
    const previous = this._fuel;
    this._fuel = Math.min(this.fuelMax, previous + amount);
    const added = this._fuel - previous;
    if (added > 0) this.emit();
    return added;
  }

  damageHull(amount: number): void {
    if (amount <= 0) return;
    const previous = this._hull;
    this._hull = Math.max(0, previous - amount);
    if (this._hull !== previous) this.emit();
  }

  repairHull(amount: number): void {
    if (amount <= 0) return;
    const previous = this._hull;
    this._hull = Math.min(this.hullMax, previous + amount);
    if (this._hull !== previous) this.emit();
  }

  damageHealth(amount: number): void {
    if (amount <= 0) return;
    const previous = this._health;
    this._health = Math.max(0, previous - amount);
    if (this._health !== previous) this.emit();
  }

  healHealth(amount: number): void {
    if (amount <= 0) return;
    const previous = this._health;
    this._health = Math.min(this.healthMax, previous + amount);
    if (this._health !== previous) this.emit();
  }

  resetAll(): void {
    const hullChanged = this._hull !== this.hullMax;
    const fuelChanged = this._fuel !== this.fuelMax;
    const healthChanged = this._health !== this.healthMax;
    const oxygenChanged = this._oxygen !== this.oxygenMax;
    this._hull = this.hullMax;
    this._fuel = this.fuelMax;
    this._health = this.healthMax;
    this._oxygen = this.oxygenMax;
    if (hullChanged || fuelChanged || healthChanged || oxygenChanged) this.emit();
  }

  /** Restore all four resources to specific values — used by the
   *  save/load system. Out-of-range values are clamped to [0, max]. */
  restore(values: {
    hull: number;
    fuel: number;
    health: number;
    oxygen: number;
  }): void {
    this._hull = clamp(values.hull, this.hullMax);
    this._fuel = clamp(values.fuel, this.fuelMax);
    this._health = clamp(values.health, this.healthMax);
    this._oxygen = clamp(values.oxygen, this.oxygenMax);
    this.emit();
  }

  subscribe(listener: StatsListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  snapshot(): PlayerStatsSnapshot {
    return {
      hull: this._hull,
      hullMax: this.hullMax,
      fuel: this._fuel,
      fuelMax: this.fuelMax,
      health: this._health,
      healthMax: this.healthMax,
      oxygen: this._oxygen,
      oxygenMax: this.oxygenMax
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const listener of this.listeners) {
      listener(snap);
    }
  }
}

function clamp(value: number, max: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > max) return max;
  return value;
}
